import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { APP_TITLE, ROUTE_TITLE_KEYS } from './route-titles';

const TITLE_PREFIX = '1P';

@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  private _title = inject(Title);
  private _translate = inject(TranslateService);
  private _subscription?: Subscription;
  private _name?: string;
  private _detail?: string;

  // Lets a page append loaded data (e.g. initiative description) to its title; reset on every navigation.
  setDetail(detail?: string): void {
    this._detail = detail?.trim() || undefined;
    this._applyTitle();
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this._subscription?.unsubscribe();
    this._detail = undefined;

    const explicitTitle = this.buildTitle(snapshot);
    if (explicitTitle) {
      this._setTitle(explicitTitle);
      return;
    }

    const key = this._resolveKey(snapshot);
    if (!key) {
      this._setTitle();
      return;
    }

    // stream() re-emits on language change so the tab title follows the selected language
    this._subscription = this._translate.stream(key).subscribe((name) => this._setTitle(name));
  }

  private _resolveKey(snapshot: RouterStateSnapshot): string | undefined {
    let route: ActivatedRouteSnapshot = snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const segments = route.pathFromRoot
      .map((r) => r.routeConfig?.path)
      .filter((p): p is string => !!p)
      .join('/')
      .split('/');
    const path = segments.join('/');
    const sharedPath = '*/' + segments.slice(1).join('/');

    return ROUTE_TITLE_KEYS[path] ?? ROUTE_TITLE_KEYS[sharedPath];
  }

  private _setTitle(name?: string): void {
    this._name = name;
    this._applyTitle();
  }

  private _applyTitle(): void {
    const parts = [this._name, this._detail].filter((p): p is string => !!p);
    this._title.setTitle(parts.length ? [TITLE_PREFIX, ...parts].join(' - ') : APP_TITLE);
  }
}
